/**
 * Cpagelayout Component
 * Generic page layout with responsive navbar, background gradient, and modal overlay system
 */
'use client';
import type { ReactElement, ReactNode } from 'react';
import { useRef } from 'react';
import dynamic from 'next/dynamic';
import { LayoutActionsProvider, ScrollContainerProvider, } from '@/contexts/layout';
import { ModalLocation } from '@/constants/componentPresets';
import type { TeamDetail } from '@cabal/shared/domain/session/team/types.js';
import { useViewport, useViewportMetrics } from '@/hooks/dom';
import { Viewport } from '@/theme/breakpoints';
import { dataEl, type DataElScope } from '@/utils/dataEl';
import type { CusermenuProps } from '../Cusermenu';
import type { CsessionsProps } from '../Csessions';
import { CpagelayoutModals } from './CpagelayoutModals';
import { CpagelayoutNavbar } from './CpagelayoutNavbar';
import { buildContentStyle, buildRootStyle, getStageClassName, } from './Cpagelayout.styles';
import { useCpagelayoutModalState } from './useCpagelayoutModalState';
import { useCpagelayoutNavigation } from './useCpagelayoutNavigation';
import { useCpagelayoutRenderDiagnostics } from './useCpagelayoutRenderDiagnostics';
import { useScrollActivity } from './useScrollActivity';
import { useScrollableContentReset } from './useScrollableContentReset';
const Cusermenu = dynamic<CusermenuProps>(() => import('../Cusermenu').then((module) => module.Cusermenu), { ssr: false });
const Csessions = dynamic<CsessionsProps>(() => import('../Csessions').then((module) => module.Csessions), { ssr: false });
export interface CpagelayoutProps {
    dataElScope: DataElScope;
    children: ReactNode;
    // Center modal props (for external control - auth, pricing, preflight, etc.)
    modalVisible?: boolean;
    modalLocation?: ModalLocation;
    modalContent?: ReactNode;
    onModalClose?: () => void;
    modalUseContainerBackground?: boolean;
    // Navbar customization
    profileImageSrc?: string;
    currentSessionId?: string; // For session history modal
    onHamburgerClick?: () => void;
    onProfileClick?: () => void;
    onLogoClick?: () => void;
    // Background customization
    useBlackBackground?: boolean; // If true, uses pure black instead of gradient
    useStageBackground?: boolean; // If true, applies the session stage background treatment
    // Non-logged navbar
    showNonLoggedNavbar?: boolean; // If true, shows non-logged navbar instead of regular one
    // Landing page specific
    isLandingPage?: boolean; // If true, navbar will be absolutely positioned
    // Auth loading state
    authLoading?: boolean; // If true, hides navbar until auth completes
    authError?: boolean; // If true, shows error state in logged navbar
    onStartNewSession?: () => void;
    /** Called when user wants to replicate a team config from session history */
    onReplicateTeam?: (teamConfig: TeamDetail) => void;
    /** Increment this value to request opening the user menu from parent scope */
    openUserMenuRequestId?: number;
}
export function Cpagelayout({ dataElScope, children, modalVisible: externalModalVisible, modalLocation: externalModalLocation, modalContent: externalModalContent, onModalClose: externalOnModalClose, modalUseContainerBackground: externalModalUseContainerBackground = true, profileImageSrc, currentSessionId, onHamburgerClick: externalOnHamburgerClick, onProfileClick: externalOnProfileClick, onLogoClick: externalOnLogoClick, useBlackBackground = false, useStageBackground = false, showNonLoggedNavbar = false, isLandingPage = false, authLoading = false, authError = false, onStartNewSession, onReplicateTeam, openUserMenuRequestId, }: CpagelayoutProps): ReactElement | null {
    const { viewport } = useViewport();
    const { vh, ready } = useViewportMetrics();
    const isMobile = viewport === Viewport.Mobile;
    const contentRef = useRef<HTMLElement>(null);
    const { onLoggedLogoClick, onNonLoggedLogoClick, pathname, } = useCpagelayoutNavigation({
        containerRef: contentRef,
        onLogoClick: externalOnLogoClick,
    });
    useCpagelayoutRenderDiagnostics({
        isMobile,
        pathname,
        ready,
        vh,
        viewport,
    });
    useScrollableContentReset({ containerRef: contentRef, ready });
    const isScrolling = useScrollActivity({ containerRef: contentRef });
    const modalState = useCpagelayoutModalState({
        externalModalVisible,
        onExternalHamburgerClick: externalOnHamburgerClick,
        onExternalModalClose: externalOnModalClose,
        onExternalProfileClick: externalOnProfileClick,
        onReplicateTeam,
        openUserMenuRequestId,
    });
    const isRootStagePage = isLandingPage || pathname === '/';
    const stageClassName = getStageClassName({
        isRootStagePage,
        useStageBackground,
        viewport,
    });
    const rootStyle = buildRootStyle({
        vh,
        viewport,
        useBlackBackground,
        useStageBackground,
    });
    const contentStyle = buildContentStyle({ vh, isCenterModalOpen: modalState.isCenterModalOpen });
    if (!ready) {
        return null;
    }
    return (
      <ScrollContainerProvider value={{ containerRef: contentRef }}>
        <LayoutActionsProvider value={modalState.layoutActionsValue}>
          <div data-el={dataEl(dataElScope, 'Root')} className={stageClassName} style={rootStyle}>
            <CpagelayoutNavbar dataElScope={dataElScope} authError={authError} authLoading={authLoading} isAnyModalOpen={modalState.isAnyModalOpen} isCenterModalOpen={modalState.isCenterModalOpen} isLandingPage={isLandingPage} isMobile={isMobile} isScrolling={isScrolling} isSessionsModalOpen={modalState.isSessionsModalOpen} isUserMenuModalOpen={modalState.isUserMenuModalOpen} onHamburgerClick={modalState.onHamburgerClick} onLoggedLogoClick={onLoggedLogoClick} onModalClose={modalState.onModalClose} onNonLoggedLogoClick={onNonLoggedLogoClick} onProfileClick={modalState.onProfileClick} profileImageSrc={profileImageSrc} showNonLoggedNavbar={showNonLoggedNavbar}/>

            <main data-el={dataEl(dataElScope, 'Viewport')} ref={contentRef} className="cpagelayout-content" style={contentStyle}>
              {children}
            </main>

            <CpagelayoutModals SessionsComponent={Csessions} UserMenuComponent={Cusermenu} currentSessionId={currentSessionId} displayMode={modalState.displayMode} externalModalContent={externalModalContent} externalModalLocation={externalModalLocation} externalModalUseContainerBackground={externalModalUseContainerBackground} externalModalVisible={externalModalVisible} isSearchInitiated={modalState.isSearchInitiated} isSessionsModalOpen={modalState.isSessionsModalOpen} isUserMenuModalOpen={modalState.isUserMenuModalOpen} onCenterModalClose={modalState.onCenterModalClose} onDisplayModeToggle={modalState.onDisplayModeToggle} onReplicateTeam={modalState.onReplicateTeam} onSearchQueryChange={modalState.onSearchQueryChange} onSearchToggle={modalState.onSearchToggle} onSessionCountChange={modalState.onSessionCountChange} onSessionsModalClose={modalState.onSessionsModalClose} onStartNewSession={onStartNewSession} onUserMenuModalClose={modalState.onUserMenuModalClose} requestUserMenuClose={modalState.requestUserMenuClose} onUserMenuUnsavedChangesChange={modalState.onUserMenuUnsavedChangesChange} searchQuery={modalState.searchQuery} sessionCount={modalState.sessionCount} userMenuInitialTab={modalState.userMenuInitialTab}/>
          </div>
        </LayoutActionsProvider>
      </ScrollContainerProvider>
    );
}
